import type { ReactNode } from 'react';

import BpkButton from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classnames from 'classnames';
import { redirect } from 'saddlebag-browser';

import BookOnWebsite from '../../../hotels-utils/components-hotel-card-adjacent/BookOnWebsite';
import OTALogo from '../../../hotels-utils/components-hotel-card-adjacent/OTALogo';
import { withI18n } from '../../../hotels-utils/services/i18n';
import { isShowHotelName } from '../../../hotels-utils/services/officialWebsite';
import RoomRateAmenities from '../RoomRateAmenities';

import type { HotelSearchResultsWrapperClientStrings } from '@skyscanner-internal/landing-pages-shared-types/types-shared/HotelSearchResultsWrapperProps';
import type { MbdTrackingAttributes } from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/HotelCard';
import type {
  I18n,
  Price,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/types';

import STYLES from './MainRate.module.scss';

type MainRateProps = {
  i18n: I18n;
  mainPrice: Price;
  fssBadge?: ReactNode;
  priceNode: ReactNode;
  stayPricesSummary?: ReactNode;
  pricePolicy?: ReactNode;
  url?: string;
  onClick?: Function;
  detailsUrl: string;
  goToDVEnabled?: boolean;
  clientStrings: HotelSearchResultsWrapperClientStrings;
  mbdTracking?: MbdTrackingAttributes;
};

const MainRate = ({
  clientStrings,
  detailsUrl,
  fssBadge = undefined,
  goToDVEnabled = false,
  i18n,
  mainPrice,
  mbdTracking = undefined,
  onClick = undefined,
  priceNode,
  pricePolicy = undefined,
  stayPricesSummary = undefined,
  url = undefined,
}: MainRateProps) => {
  const {
    amenities,
    funnelType,
    isOfficial,
    logoLink,
    name: partnerName,
    partnerId,
    partnerType,
  } = mainPrice;

  const showHotelName = isShowHotelName(isOfficial, partnerId, partnerType);

  const HotelNameOrLogo = showHotelName ? (
    <BookOnWebsite className={STYLES.MainRate__hotelName} />
  ) : (
    <OTALogo src={logoLink} name={partnerName} height={19} />
  );

  const logClassName =
    isOfficial && fssBadge
      ? STYLES.MainRate__logoOrName
      : classnames(STYLES.MainRate__logo);

  const { cardLowestPriceLabel, hotelCardCTALabel, hotelCardCTALabelForDV } =
    clientStrings;

  const redirectLabel = goToDVEnabled
    ? hotelCardCTALabelForDV
    : hotelCardCTALabel;

  const redirectButton = () => (
    <BpkButton
      className={STYLES.MainRate__cta}
      data-tracking-element-id={mbdTracking?.elementId}
      data-tracking-common-params={mbdTracking?.commonParams}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        onClick?.(true, mainPrice, false, 'lowestPriceBtn');
        redirect(url, true);
      }}
    >
      {redirectLabel}
    </BpkButton>
  );

  const dealButton =
    funnelType === 'dbook' ? (
      <BpkButton
        className={STYLES.MainRate__cta}
        data-tracking-element-id={mbdTracking?.elementId}
        data-tracking-common-params={mbdTracking?.commonParams}
      >
        {goToDVEnabled
          ? i18n.translate('OTARates_button_viewHotel')
          : i18n.translate('coupon_modal_view_details')}
      </BpkButton>
    ) : (
      redirectButton()
    );

  const replacementTagFormat = (text: string, index: number) => (
    <BpkText
      textStyle={TEXT_STYLES.label3}
      key={index}
      className={classnames({
        [STYLES.MainRate__specificText]:
          i18n.culture.locale === 'en-GB' || i18n.culture.locale === 'en-US',
      })}
    >
      {text}
    </BpkText>
  );

  const replacements = {
    ...{
      tag: {
        pair: '/tag',
        format: replacementTagFormat,
      },
    },
  };

  const getLowestPriceLabelContent = (): ReactNode => {
    if (!cardLowestPriceLabel) {
      return '';
    }

    if (!cardLowestPriceLabel.includes('{tag}')) {
      return (
        <BpkText textStyle={TEXT_STYLES.label3}>{cardLowestPriceLabel}</BpkText>
      );
    }

    return i18n.translate('Hotel_card_lowestPrice_label', replacements);
  };

  const getCardUrl = () =>
    funnelType === 'dbook' ? `${detailsUrl}&need_scroll=true` : detailsUrl;

  return (
    <div className={STYLES.MainRateWrapper}>
      <BpkCard
        className={STYLES.MainRate}
        onClick={() => {
          onClick?.(true, mainPrice, true, 'stub');
        }}
        target="_blank"
        rel="nofollow"
        href={getCardUrl()}
        padded={false}
      >
        <div className={STYLES.MainRate__rate}>
          <div className={STYLES.MainRate__lowestPriceLabel}>
            <div className={STYLES.MainRate__text}>
              <BpkText
                textStyle={TEXT_STYLES.caption}
                className={STYLES.MainRate__lowestPrice}
              >
                {getLowestPriceLabelContent()}
              </BpkText>
            </div>
          </div>
          <div className={logClassName}>{HotelNameOrLogo}</div>
          {fssBadge || priceNode}
          <div>
            {stayPricesSummary}
            {pricePolicy}
          </div>
        </div>
        <div className={STYLES.MainRate__ctaAndAmenity}>
          <RoomRateAmenities amenities={amenities} />
          <div className={STYLES.MainRate__ctaButton}>{dealButton}</div>
        </div>
      </BpkCard>
    </div>
  );
};

export default withI18n(MainRate);
